// ExecAttendance.js — "การเข้าเรียนและการสอน" (pos 90/99)
//
// คำถามของหน้านี้: ห้องไหนขาดเรียนเรื้อรัง และครูคนไหนไม่เช็คชื่อ/ไม่เข้าสอน
// สองเรื่องนี้อยู่หน้าเดียวกันโดยตั้งใจ — ห้องที่ไม่มีใครเช็คชื่อเลยจะดูเหมือน
// มาเรียนครบ 100% ถ้าดูแต่อัตราขาด
//
// ทั้งหน้าสลับภาคด้วยแท็บบนสุด แบบเดียวกับหน้ากลุ่มเสี่ยง ไม่ใช่เอาสองภาคมาปนกันใน
// ตารางเดียว — ห้อง ครู และระดับชั้นของสองภาคเป็นคนละชุดกันทั้งหมด (ห้อง "5/1" ของ
// ศาสนาไม่ใช่ ม.5/1 ของสามัญ) ตารางที่ปนกันจะอ่านไม่ออกว่าแถวไหนของใคร
// แต่กราฟแนวโน้มยังลากเส้นของอีกภาคไว้เทียบเสมอ เพราะ "ศาสนาต่ำกว่าสามัญไหม" ต้องตอบได้
// โดยไม่ต้องกดสลับแท็บกลับไปกลับมาแล้วจำตัวเลขเอง
import React, { useMemo, useState } from "react";
import { Alert, Badge, Col, Nav, ProgressBar, Row, Table } from "react-bootstrap";
import ReactApexChart from "react-apexcharts";
import {
  ExecPage, Panel, SectionTitle, StatCard, EmptyState, ScrollTable,
  YearPicker, PeriodPicker, useExecYear, useExecData,
  fmtNum, fmtPct, toNum, fmtThaiDate, fmtThaiFullDate, todayKey, shiftDay, MONTH_NAMES,
} from "./ExecShell";

// วันในสัปดาห์กำกับตารางรายวัน — ผู้บริหารอ่านแถวแล้วต้องรู้ทันทีว่าวันศุกร์
// (วันก่อนกลับบ้าน) หรือวันจันทร์ ไม่ใช่ต้องไปเปิดปฏิทินเทียบเอง
const WEEKDAY_NAMES = ["อา.", "จ.", "อ.", "พ.", "พฤ.", "ศ.", "ส."];

const weekdayOf = (value) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : WEEKDAY_NAMES[date.getDay()];
};

// สีประจำภาค ใช้ซ้ำทั้งเส้นกราฟและแท่ง เพื่อให้จำได้ว่าสีเขียวคือภาคศาสนาเสมอ
const SIDE_COLOR = { academic: "#0d6efd", islamic: "#198754" };
const SIDE_LABEL = { academic: "ภาคสามัญ", islamic: "ภาคศาสนา" };

export default function ExecAttendance() {
  const { years, yearId, setYearId } = useExecYear();
  const [rangeKey, setRangeKey] = useState("30");
  const [rangeDays, setRangeDays] = useState(30);
  // โหมด "day" = ทั้งหน้าเป็นของวันเดียว ไม่ใช่แค่กราฟ — ห้องที่ต้องติดตามกับวินัย
  // การเช็คชื่อของครูก็เป็นของวันนั้น เพราะคำถามคือ "วันนี้มีอะไรต้องสั่งการ"
  const [mode, setMode] = useState("range");
  const [day, setDay] = useState(todayKey);
  // รายเดือนตอบ "ปีนี้เป็นอย่างไรเทียบปีก่อน" ส่วนรายวันตอบ "วันไหนผิดปกติ"
  // คนละคำถามแต่เป็นตัวเลขชุดเดียวกัน จึงสลับในกล่องเดียวแทนที่จะมีสองกราฟ
  const [trendMode, setTrendMode] = useState("month");
  const [side, setSide] = useState("academic");

  const selectRange = (key, days) => {
    setMode("range");
    setRangeKey(key);
    setRangeDays(days);
  };

  // เลือกวันแล้วสลับกราฟไปรายวันให้เลย — คนที่กด "วันนี้" กำลังคิดเป็นวัน
  // ไม่ใช่เป็นเดือน แต่ยังกดกลับไปดูรายเดือนได้ถ้าอยากเห็นภาพทั้งปี
  const selectDay = (value) => {
    setMode("day");
    setDay(value);
    setTrendMode("day");
  };

  const query = useMemo(() => {
    if (mode === "day") return { year_id: yearId, from: day, to: day };
    const to = todayKey();
    return { year_id: yearId, from: shiftDay(to, -(rangeDays - 1)), to };
  }, [yearId, mode, day, rangeDays]);

  // ข้อความกำกับช่วงเวลา ใช้ซ้ำทั้งใบตัวเลขและหัวกล่อง ไม่ให้แต่ละที่เขียนเอง
  const periodLabel = mode === "day"
    ? (day === todayKey() ? `วันนี้ · ${fmtThaiFullDate(day)}` : fmtThaiFullDate(day))
    : `${rangeDays} วันล่าสุด`;

  const { data, loading, error } = useExecData("/api/executive/stats/attendance", query);

  /* ── ข้อมูลดิบของทั้งสองภาค ──────────────────────────────────────────────
     ค่าที่กราฟใช้เป็น dependency ต้องมี identity คงที่ระหว่าง render ไม่งั้น useMemo
     ด้านล่างคำนวณใหม่ทุกครั้งที่หน้า re-render */
  const islamic = useMemo(() => (data?.islamic || { available: false }), [data]);
  const monthly = useMemo(() => (Array.isArray(data?.monthly) ? data.monthly : []), [data]);
  const previousMonthly = useMemo(() => (Array.isArray(data?.previousMonthly) ? data.previousMonthly : []), [data]);
  const islamicMonthly = useMemo(() => (data?.islamicMonthly || { available: false, rows: [] }), [data]);
  const islamicPreviousMonthly = useMemo(() => (data?.islamicPreviousMonthly || { available: false, rows: [] }), [data]);
  const byLevel = useMemo(() => (Array.isArray(data?.byLevel) ? data.byLevel : []), [data]);
  const daily = useMemo(() => (Array.isArray(data?.daily) ? data.daily : []), [data]);
  const islamicDaily = useMemo(() => (data?.islamicDaily || { available: false, rows: [] }), [data]);
  const dailyRoomCheck = useMemo(() => (data?.dailyRoomCheck || { totalRooms: 0, days: [] }), [data]);
  const islamicRoomCheck = useMemo(() => (data?.islamicDailyRoomCheck || { available: false, totalRooms: 0, days: [] }), [data]);
  const substitution = data?.substitution || { available: false };

  // ภาคศาสนายังไม่มีข้อมูล (ตารางของภาคนั้นยังไม่ได้ deploy หรือปีที่เลือกยังไม่มีการ
  // เช็คชื่อเลย) — ปิดแท็บไว้ และถ้าเลือกค้างไว้แล้วเปลี่ยนปี ให้ตกกลับมาภาคสามัญ
  // แทนที่จะโชว์หน้าที่เป็นขีดทั้งหน้าจนดูเหมือนระบบพัง
  const islamicAvailable = islamic.available !== false;
  const activeSide = side === "islamic" && islamicAvailable ? "islamic" : "academic";
  const isIslamic = activeSide === "islamic";
  const sideLabel = SIDE_LABEL[activeSide];
  const otherLabel = SIDE_LABEL[isIslamic ? "academic" : "islamic"];

  /* ── ชุดข้อมูลของภาคที่กำลังดู ───────────────────────────────────────────── */
  const summary = (isIslamic ? islamic.summary : data?.summary) || {};
  const teacherMiss = (isIslamic ? islamic.teacherMiss : data?.teacherMiss) || { top: [] };
  const worstRooms = (isIslamic ? islamic.worstRooms : data?.worstRooms) || [];
  const roomCheck = isIslamic ? islamicRoomCheck : dailyRoomCheck;

  const sideDaily = useMemo(
    () => (isIslamic ? (islamicDaily.rows || []) : daily),
    [isIslamic, islamicDaily, daily]
  );
  const otherDaily = useMemo(
    () => (isIslamic ? daily : (islamicDaily.rows || [])),
    [isIslamic, islamicDaily, daily]
  );

  // ระดับชั้นของสองภาคไม่เหมือนกัน: สามัญเป็น ม.1–ม.6 ส่วนศาสนาจัดกลุ่มตามผู้รับผิดชอบ
  // (ตอนต้น/ตอนกลาง/ตอนปลาย/AIP/STP) ไม่ใช่รายชั้นทั้ง 16 ชั้นที่อ่านเป็นภาพรวมไม่ได้
  // c_class ของสามัญเก็บเป็นชื่อเต็ม ("ชั้นมัธยมศึกษาปีที่ 1") ซึ่งยาวเกินกว่าจะเป็น
  // ป้ายแกนกราฟ จึงย่อเป็น "ม.1" แบบเดียวกับที่ทั้งระบบใช้
  const levelRows = useMemo(() => (isIslamic
    ? (islamic.byLevel || []).map((row) => ({
      key: row.key, label: row.label, presentRate: toNum(row.presentRate), total: toNum(row.total),
    }))
    : byLevel.map((row) => ({
      key: row.c_id, label: `ม.${row.c_id}`, presentRate: toNum(row.presentRate), total: toNum(row.total),
    }))
  ), [isIslamic, islamic, byLevel]);

  // เมื่อเลือกวันเดียว server ขยายช่วงของกราฟรายวันเป็นสองสัปดาห์จบที่วันนั้น
  // (จุดเดียวไม่บอกว่าตัวเลขของวันนี้ปกติหรือผิดปกติ) กำกับให้ชัดว่าคนละช่วงกับ
  // ตัวเลขสรุปด้านบน ไม่ใช่ปล่อยให้เดาเอง
  const dailyRangeLabel = mode === "day"
    ? `${fmtThaiDate(data?.dailyRange?.from)} – ${fmtThaiDate(data?.dailyRange?.to)}`
    : periodLabel;

  // เส้นปีนี้กับปีก่อนทับกัน แล้วเติมเส้นของอีกภาคเป็นตัวเทียบข้ามภาค
  const monthlySeries = useMemo(() => {
    const mapOf = (rows) => {
      const map = {};
      (rows || []).forEach((row) => { map[toNum(row.month)] = row; });
      return map;
    };
    const cur = mapOf(isIslamic ? islamicMonthly.rows : monthly);
    const prev = mapOf(isIslamic ? islamicPreviousMonthly.rows : previousMonthly);
    const other = mapOf(isIslamic ? monthly : islamicMonthly.rows);
    const pick = (map) => MONTH_NAMES.map((_, i) => map[i + 1]?.presentRate ?? null);

    const series = [{ name: `${sideLabel} ปีนี้`, data: pick(cur) }];
    if (Object.keys(prev).length > 0) series.push({ name: `${sideLabel} ปีก่อน`, data: pick(prev) });
    if (Object.keys(other).length > 0) series.push({ name: `${otherLabel} ปีนี้`, data: pick(other) });
    return series;
  }, [isIslamic, monthly, previousMonthly, islamicMonthly, islamicPreviousMonthly, sideLabel, otherLabel]);

  const monthlyOptions = useMemo(() => ({
    chart: { type: "line", toolbar: { show: false } },
    colors: [SIDE_COLOR[activeSide], "#adb5bd", SIDE_COLOR[isIslamic ? "academic" : "islamic"]],
    stroke: { curve: "smooth", width: [3, 2, 2], dashArray: [0, 5, 0] },
    dataLabels: { enabled: false },
    markers: { size: 3 },
    legend: { position: "top", fontSize: "12px" },
    xaxis: { categories: MONTH_NAMES, labels: { style: { fontSize: "11px" } } },
    yaxis: { min: 50, max: 100, labels: { formatter: (v) => `${Math.round(v)}%`, style: { colors: "#6e7985", fontSize: "11px" } } },
    grid: { borderColor: "rgba(72,94,144,0.08)" },
    tooltip: { shared: true, intersect: false, y: { formatter: (v) => (v == null ? "ไม่มีข้อมูล" : `${v}%`) } },
  }), [activeSide, isIslamic]);

  // รายวันรวม % ห้องที่เช็คชื่อของวันนั้นเข้ามาด้วย เพราะเป็นใจความของทั้งหน้า:
  // วันที่มีไม่กี่ห้องเช็คชื่อ อัตรามาเรียนจะดูสวยทั้งที่ไม่รู้เรื่องเด็กอีกครึ่งโรงเรียน
  const dailyRows = useMemo(() => {
    const checkByDate = {};
    (roomCheck.days || []).forEach((d) => { checkByDate[d.att_date] = d; });
    const otherByDate = {};
    otherDaily.forEach((d) => { otherByDate[d.att_date] = d; });
    return sideDaily.map((row) => ({
      ...row,
      checkedRooms: checkByDate[row.att_date]?.checked ?? null,
      checkPercent: checkByDate[row.att_date]?.percent ?? null,
      otherPresentRate: otherByDate[row.att_date]?.presentRate ?? null,
    }));
  }, [sideDaily, otherDaily, roomCheck]);

  // ไม่มีแถวของอีกภาคสักวันในช่วงนี้ = ไม่ต้องมีคอลัมน์/เส้นที่มีแต่ขีด
  const hasOtherDaily = otherDaily.length > 0;

  const dailySeries = useMemo(() => {
    const series = [{ name: sideLabel, data: dailyRows.map((row) => row.presentRate) }];
    if (hasOtherDaily) {
      series.push({ name: otherLabel, data: dailyRows.map((row) => row.otherPresentRate) });
    }
    return series;
  }, [dailyRows, hasOtherDaily, sideLabel, otherLabel]);

  const dailyOptions = useMemo(() => {
    // แกน Y ของรายเดือนตรึงไว้ที่ 50–100 ได้เพราะค่าเฉลี่ยเดือนไม่แกว่ง แต่รายวัน
    // ตกลงไปถึง 60 หรือต่ำกว่าได้ในวันผิดปกติ ซึ่งเป็นวันที่ต้องเห็นชัดที่สุด
    const values = dailyRows
      .flatMap((row) => [row.presentRate, row.otherPresentRate])
      .filter((v) => v !== null && v !== undefined);
    const lowest = values.length > 0 ? Math.min(...values) : 100;
    return {
      chart: { type: "line", toolbar: { show: false }, zoom: { enabled: false } },
      colors: [SIDE_COLOR[activeSide], SIDE_COLOR[isIslamic ? "academic" : "islamic"]],
      stroke: { curve: "straight", width: [2, 2], dashArray: [0, 4] },
      dataLabels: { enabled: false },
      markers: { size: 3 },
      legend: { position: "top", fontSize: "12px" },
      xaxis: {
        categories: dailyRows.map((row) => `${weekdayOf(row.att_date)} ${fmtThaiDate(row.att_date)}`),
        labels: { style: { fontSize: "10px" }, rotate: -45, hideOverlappingLabels: true },
        tooltip: { enabled: false },
      },
      yaxis: {
        min: Math.max(0, Math.min(50, Math.floor(lowest / 10) * 10)),
        max: 100,
        labels: { formatter: (v) => `${Math.round(v)}%`, style: { colors: "#6e7985", fontSize: "11px" } },
      },
      grid: { borderColor: "rgba(72,94,144,0.08)" },
      tooltip: { shared: true, intersect: false, y: { formatter: (v) => (v == null ? "ไม่มีข้อมูล" : `${v}%`) } },
    };
  }, [dailyRows, activeSide, isIslamic]);

  const roomCheckOptions = useMemo(() => ({
    chart: { type: "area", toolbar: { show: false } },
    colors: ["#0dcaf0"],
    stroke: { curve: "smooth", width: 2 },
    fill: { type: "gradient", gradient: { shadeIntensity: 1, opacityFrom: 0.3, opacityTo: 0.05 } },
    dataLabels: { enabled: false },
    xaxis: {
      categories: (roomCheck.days || []).map((d) => fmtThaiDate(d.att_date)),
      labels: { style: { fontSize: "10px" }, rotate: -45, hideOverlappingLabels: true },
    },
    yaxis: { min: 0, max: 100, labels: { formatter: (v) => `${Math.round(v)}%`, style: { colors: "#6e7985", fontSize: "11px" } } },
    grid: { borderColor: "rgba(72,94,144,0.08)" },
    tooltip: { y: { formatter: (v) => `${v}% ของห้องทั้งหมด` } },
  }), [roomCheck]);

  const levelOptions = useMemo(() => ({
    chart: { type: "bar", toolbar: { show: false } },
    colors: [SIDE_COLOR[activeSide]],
    plotOptions: { bar: { borderRadius: 3, columnWidth: "50%", distributed: false } },
    dataLabels: { enabled: true, formatter: (v) => `${v}%`, style: { fontSize: "10px" } },
    legend: { show: false },
    xaxis: { categories: levelRows.map((row) => row.label), labels: { style: { fontSize: "11px" } } },
    yaxis: { min: 50, max: 100, labels: { formatter: (v) => `${Math.round(v)}%`, style: { colors: "#6e7985", fontSize: "11px" } } },
    grid: { borderColor: "rgba(72,94,144,0.08)" },
    // จำนวนรายการเช็คชื่ออยู่ใน tooltip ไม่ใช่บนแท่ง — แท่งที่มาจากการเช็คไม่กี่รายการ
    // ต้องอ่านด้วยความระวัง แต่ไม่ควรรกจนบังตัวเลขหลัก
    tooltip: {
      y: {
        formatter: (v, ctx) => `${v}% · เช็คชื่อ ${fmtNum(levelRows[ctx?.dataPointIndex]?.total)} รายการ`,
      },
    },
  }), [levelRows, activeSide]);

  const missRate = teacherMiss.missRate;

  return (
    <ExecPage
      title="การเข้าเรียนและการสอน"
      icon="ri-calendar-check-line"
      lead="อัตรามาเรียนเทียบปีก่อน ห้องที่ขาดเรียนสูงสุด และวินัยการเช็คชื่อของครู แยกภาคสามัญ/ภาคศาสนา"
      activeYear={data?.year}
      loading={loading}
      error={error}
      toolbar={
        <>
          <PeriodPicker mode={mode} rangeKey={rangeKey} day={day} onRange={selectRange} onDay={selectDay} />
          <YearPicker years={years} yearId={yearId} onChange={setYearId} activeYear={data?.year} />
        </>
      }
    >
      <Nav variant="pills" className="mb-3 gap-2" activeKey={activeSide} onSelect={(key) => setSide(key || "academic")}>
        <Nav.Item><Nav.Link eventKey="academic">ภาคสามัญ</Nav.Link></Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="islamic" disabled={!islamicAvailable}>
            ภาคศาสนา
            {!islamicAvailable && <span className="fs-xs ms-1">(ยังไม่มีข้อมูล)</span>}
          </Nav.Link>
        </Nav.Item>
      </Nav>

      {isIslamic && (
        <div className="alert alert-light border fs-xs py-2 px-3 mb-3">
          ตัวเลขชุดนี้มาจากรายงานตัวเดียวกับที่หัวหน้าวิชาการศาสนาเห็น — นับคาบตามตารางสอนศาสนา
          และหักคาบที่ปฏิทินระบุว่างดสอนทั้งโรงเรียนแล้ว ระดับชั้นจัดกลุ่มตามผู้รับผิดชอบ
          (ตอนต้น ชั้น 2–4 · ตอนกลาง 5–7 · ตอนปลาย 8–10 · ห้องพิเศษ AIP และ STP แยกต่างหาก)
        </div>
      )}

      {/* วันเดียวที่ไม่มีการเช็คชื่อเลยทำให้ทั้งหน้าเป็นขีดกับศูนย์ ซึ่งอ่านเหมือน
          ระบบพัง ต้องบอกให้ชัดว่าไม่มีข้อมูล ไม่ใช่ "ไม่มีใครมาเรียน" */}
      {mode === "day" && toNum(summary.total) === 0 && (
        <Alert variant="secondary" className="d-flex align-items-center gap-2">
          <i className="ri-calendar-close-line" style={{ fontSize: 20 }} />
          <span>
            {sideLabel}ไม่มีการเช็คชื่อใน{periodLabel} — อาจเป็นวันหยุดหรือยังไม่มีครูคนไหนเช็คชื่อ
            ลองเลื่อนไปวันก่อนหน้าด้วยปุ่มลูกศร
          </span>
        </Alert>
      )}

      {/* ห้าใบ = ห้าสถานะที่ต้องอ่านคู่กัน จึงเรียงในแถวเดียวด้วย row-cols แทน
          Col แบบ xl={3} ที่หารไม่ลงตัวแล้วเหลือใบเดียวห้อยบรรทัดที่สอง */}
      <Row xs={1} sm={2} xl={5} className="g-3 mb-4">
        <Col>
          <StatCard
            icon="ri-user-follow-fill"
            color={toNum(summary.presentRate) >= 90 ? "success" : "warning"}
            label={`อัตรามาเรียน${sideLabel} ${periodLabel}`}
            value={fmtPct(summary.presentRate)}
            sub={`เช็คชื่อ ${fmtNum(summary.total)} รายการ · ${fmtNum(summary.schoolDays)} วันเรียน`}
          />
        </Col>
        {/* ลาแยกจากขาดเป็นใบของตัวเอง — การลาคือเด็กที่ผู้ปกครอง/ครูรับรู้แล้ว
            คนละเรื่องกับการหายไปเฉยๆ
            เกณฑ์มาจากของจริงในฐาน: เดือนปกติอยู่ราว 4–7% ของรายการเช็คชื่อ
            เกิน 10% คือเดือนที่ลากันทั้งโรงเรียนจนควรถามว่าเกิดอะไรขึ้น */}
        <Col>
          <StatCard
            icon="ri-calendar-todo-fill"
            color={toNum(summary.leaveRate) >= 10 ? "warning" : "info"}
            label="อัตราการลา"
            value={fmtPct(summary.leaveRate)}
            sub={`ลา ${fmtNum(summary.leave)} จาก ${fmtNum(summary.total)} รายการ`}
          />
        </Col>
        <Col>
          <StatCard
            icon="ri-user-unfollow-fill"
            color={toNum(summary.absentRate) >= 10 ? "danger" : "secondary"}
            label="อัตราขาดเรียน"
            value={fmtPct(summary.absentRate)}
            sub={`ขาด ${fmtNum(summary.absent)} จาก ${fmtNum(summary.total)} รายการ`}
          />
        </Col>
        {/* สายคือเด็กที่มาโรงเรียน คนละกลุ่มกับขาด จึงไม่รวมอยู่ในใบขาดเรียน
            ปกติอยู่ราว 1% เท่านั้น เกิน 3% ถือว่าผิดปกติแล้ว */}
        <Col>
          <StatCard
            icon="ri-time-fill"
            color={toNum(summary.lateRate) >= 3 ? "warning" : "secondary"}
            label="อัตรามาสาย"
            value={fmtPct(summary.lateRate)}
            sub={`สาย ${fmtNum(summary.late)} จาก ${fmtNum(summary.total)} รายการ`}
          />
        </Col>
        <Col>
          <StatCard
            icon="ri-close-circle-fill"
            color={toNum(missRate) > 10 ? "danger" : toNum(missRate) > 5 ? "warning" : "success"}
            label={`คาบ${isIslamic ? "ศาสนา" : "สามัญ"}ที่ไม่ได้เช็คชื่อ`}
            value={fmtPct(missRate)}
            sub={`${fmtNum(teacherMiss.missedSlots)} จาก ${fmtNum(teacherMiss.totalSlots)} คาบ · ครู ${fmtNum(teacherMiss.teachersWithMiss)} คน`}
            tone={toNum(missRate) > 10 ? "danger" : undefined}
          />
        </Col>
      </Row>

      <Row className="g-3 mb-4">
        <Col xl={7}>
          <Panel
            title={`อัตรามาเรียน ${sideLabel}`}
            note={trendMode === "month" ? `รายเดือน ปีนี้เทียบปีก่อน และ${otherLabel}` : `รายวัน · ${dailyRangeLabel}`}
            action={
              <div className="btn-group btn-group-sm" role="group" aria-label="มุมมองแนวโน้ม">
                <button
                  type="button"
                  className={`btn btn-${trendMode === "month" ? "primary" : "outline-secondary"}`}
                  onClick={() => setTrendMode("month")}
                >
                  รายเดือน
                </button>
                <button
                  type="button"
                  className={`btn btn-${trendMode === "day" ? "primary" : "outline-secondary"}`}
                  onClick={() => setTrendMode("day")}
                >
                  รายวัน
                </button>
              </div>
            }
          >
            {trendMode === "month" ? (
              monthlySeries[0].data.some((v) => v !== null && v !== undefined) ? (
                <ReactApexChart options={monthlyOptions} series={monthlySeries} type="line" height={300} />
              ) : <EmptyState message={`ยังไม่มีข้อมูลรายเดือนของ${sideLabel}`} />
            ) : (
              dailyRows.length > 0 ? (
                <ReactApexChart options={dailyOptions} series={dailySeries} type="line" height={300} />
              ) : <EmptyState message={`ยังไม่มีข้อมูลรายวันของ${sideLabel}ในช่วงนี้`} />
            )}
          </Panel>
        </Col>
        <Col xl={5}>
          <Panel
            title={isIslamic ? "อัตรามาเรียนรายระดับ" : "อัตรามาเรียนรายระดับชั้น"}
            note={isIslamic ? `${periodLabel} · ตอนต้น/ตอนกลาง/ตอนปลาย และห้องพิเศษ` : periodLabel}
          >
            {levelRows.length > 0 ? (
              <ReactApexChart
                options={levelOptions}
                series={[{ name: "มาเรียน", data: levelRows.map((row) => row.presentRate) }]}
                type="bar" height={300}
              />
            ) : <EmptyState message={`ยังไม่มีข้อมูลรายระดับชั้นของ${sideLabel}`} />}
          </Panel>
        </Col>
      </Row>

      {trendMode === "day" && (
        <>
          <SectionTitle
            icon="ri-calendar-2-line"
            title={`รายวัน ${sideLabel}`}
            note={`เรียงจากวันล่าสุด · ${dailyRangeLabel}`}
            color="info"
          />
          <Row className="g-3 mb-4">
            <Col xs={12}>
              <Panel note="อ่านคู่กับคอลัมน์ขวาสุดเสมอ — วันที่มีห้องเช็คชื่อไม่ครบ อัตรามาเรียนจะสูงเกินจริงเพราะไม่รู้เรื่องห้องที่ไม่ได้เช็ค">
                {dailyRows.length === 0 ? <EmptyState message={`ยังไม่มีข้อมูลรายวันของ${sideLabel}ในช่วงนี้`} /> : (
                  <div style={{ maxHeight: 460, overflowY: "auto" }}>
                    <ScrollTable>
                      <Table className="table-sm mb-0 align-middle">
                        <thead className="position-sticky top-0 bg-body" style={{ zIndex: 1 }}>
                          <tr>
                            <th>วันที่</th>
                            <th className="text-end">มาเรียน</th>
                            {hasOtherDaily && <th className="text-end">{otherLabel}</th>}
                            <th className="text-end">ขาด</th>
                            <th className="text-end">สาย</th>
                            <th className="text-end">ลา</th>
                            <th className="text-end">เช็คชื่อ</th>
                            <th className="text-end">ห้องที่เช็คชื่อ</th>
                          </tr>
                        </thead>
                        <tbody>
                          {[...dailyRows].reverse().map((row) => (
                            // แถวของวันที่เลือกอยู่ไฮไลต์ไว้ เพราะตารางกว้างกว่าช่วงที่เลือก
                            // ต้องเห็นทันทีว่าแถวไหนคือวันที่ตัวเลขด้านบนพูดถึง
                            <tr key={row.att_date} className={mode === "day" && row.att_date === day ? "table-active" : ""}>
                              <td className="fw-semibold text-nowrap">
                                {weekdayOf(row.att_date)} {fmtThaiDate(row.att_date)}
                                {mode === "day" && row.att_date === day && (
                                  <Badge bg="primary" className="ms-2 fw-normal">วันที่เลือก</Badge>
                                )}
                              </td>
                              <td className="text-end">
                                <Badge bg={toNum(row.presentRate) >= 90 ? "success" : toNum(row.presentRate) >= 80 ? "warning" : "danger"}>
                                  {fmtPct(row.presentRate)}
                                </Badge>
                              </td>
                              {hasOtherDaily && (
                                <td className="text-end text-secondary">{fmtPct(row.otherPresentRate)}</td>
                              )}
                              <td className="text-end fw-semibold">{fmtNum(row.absent)}</td>
                              <td className="text-end">{fmtNum(row.late)}</td>
                              <td className="text-end">{fmtNum(row.leave)}</td>
                              <td className="text-end text-secondary">{fmtNum(row.total)}</td>
                              <td className="text-end">
                                <span className={toNum(row.checkPercent) < 60 ? "text-danger fw-semibold" : "text-secondary"}>
                                  {fmtNum(row.checkedRooms)}/{fmtNum(roomCheck.totalRooms)} · {fmtPct(row.checkPercent)}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </ScrollTable>
                  </div>
                )}
              </Panel>
            </Col>
          </Row>
        </>
      )}

      <SectionTitle
        icon="ri-error-warning-line"
        title={`ห้องที่ต้องติดตาม ${sideLabel}`}
        note={`เรียงตามอัตราขาดเรียน · ${periodLabel}`}
        color="danger"
      />
      <Row className="g-3 mb-4">
        <Col xs={12}>
          <Panel note="นับเฉพาะห้องที่มีรายการเช็คชื่อมากพอให้อัตราเชื่อถือได้">
            {worstRooms.length === 0 ? <EmptyState message={`ไม่มีห้อง${sideLabel}ที่เข้าเกณฑ์ต้องติดตาม`} /> : (
              <ScrollTable>
                <Table className="table-sm mb-0 align-middle">
                  <thead>
                    <tr>
                      <th>ห้อง</th>
                      <th>ครูประจำชั้น</th>
                      <th className="text-end">ขาด</th>
                      <th className="text-end">สาย</th>
                      {/* ห้องศาสนาที่เช็คชื่อไปไม่กี่คาบ อัตราขาดจะมาจากกลุ่มตัวอย่างเล็ก
                          คอลัมน์นี้บอกว่าอัตราข้างๆ มาจากการเช็คกี่คาบจากที่ควรเช็ค */}
                      {isIslamic && <th className="text-end">คาบที่เช็ค</th>}
                      <th style={{ minWidth: 130 }}>อัตราขาด</th>
                    </tr>
                  </thead>
                  <tbody>
                    {worstRooms.map((room) => (
                      <tr key={room.r_id ?? room.ir_id}>
                        <td className="fw-semibold">{room.room_label}</td>
                        <td className="text-secondary fs-xs">{room.advisor_names || "ยังไม่กำหนด"}</td>
                        <td className="text-end">{fmtNum(room.absent)}</td>
                        <td className="text-end">{fmtNum(room.late)}</td>
                        {isIslamic && (
                          <td className="text-end text-secondary fs-xs">
                            {fmtNum(room.checkedSlots)}/{fmtNum(room.expectedSlots)}
                          </td>
                        )}
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <ProgressBar
                              className="flex-fill" style={{ height: 7 }}
                              now={Math.min(100, toNum(room.absentRate) * 2)}
                              variant={toNum(room.absentRate) >= 20 ? "danger" : toNum(room.absentRate) >= 10 ? "warning" : "secondary"}
                            />
                            <span className="fs-xs fw-semibold" style={{ minWidth: 42 }}>{fmtPct(room.absentRate)}</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </ScrollTable>
            )}
          </Panel>
        </Col>
      </Row>

      <SectionTitle
        icon="ri-user-star-line"
        title={`วินัยการสอนของครู ${sideLabel}`}
        note="คาบที่มีตารางสอนแต่ไม่มีการเช็คชื่อ หักวันหยุดและคาบที่ปฏิทินระบุว่างดสอนแล้ว"
        color="warning"
      />
      <Row className="g-3 mb-4">
        <Col xl={7}>
          <Panel title="ครูที่มีคาบไม่ได้เช็คชื่อมากที่สุด">
            {(teacherMiss.top || []).length === 0 ? (
              <EmptyState message={`ไม่มีคาบ${isIslamic ? "ศาสนา" : "สามัญ"}ที่ขาดการเช็คชื่อในช่วงนี้`} />
            ) : (
              <ScrollTable>
                <Table className="table-sm mb-0 align-middle">
                  <thead>
                    <tr>
                      <th>ครู</th>
                      <th className="text-end">คาบทั้งหมด</th>
                      <th className="text-end">ไม่ได้เช็ค</th>
                      <th className="text-end">สัดส่วน</th>
                    </tr>
                  </thead>
                  <tbody>
                    {teacherMiss.top.map((t) => (
                      <tr key={t.t_id ?? t.t_name}>
                        <td>{t.t_name}</td>
                        <td className="text-end">{fmtNum(t.slots)}</td>
                        <td className="text-end fw-semibold text-danger">{fmtNum(t.missed)}</td>
                        <td className="text-end">
                          <Badge bg={toNum(t.missRate) >= 30 ? "danger" : toNum(t.missRate) >= 15 ? "warning" : "secondary"}>
                            {fmtPct(t.missRate)}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </ScrollTable>
            )}
          </Panel>
        </Col>
        <Col xl={5}>
          <Panel
            title="สัดส่วนห้องที่เช็คชื่อรายวัน"
            note={`${sideLabel} · จากห้องทั้งหมด ${fmtNum(roomCheck.totalRooms)} ห้อง`}
          >
            {(roomCheck.days || []).length === 0 ? <EmptyState compact /> : (
              <ReactApexChart
                options={roomCheckOptions}
                series={[{ name: "ห้องที่เช็คชื่อ", data: (roomCheck.days || []).map((d) => toNum(d.percent)) }]}
                type="area" height={240}
              />
            )}
            {/* การจัดครูสอนแทนมีเฉพาะภาคสามัญ (tb_substitution ผูกกับ tb_teaching)
                จึงไม่ขึ้นในแท็บศาสนา แทนที่จะโชว์ศูนย์ให้เข้าใจผิดว่าไม่เคยจัดครูสอนแทน */}
            {!isIslamic && substitution.available && (
              <div className="border-top pt-3 mt-3 fs-sm">
                <div className="d-flex justify-content-between">
                  <span className="text-secondary">คาบที่มีครูสอนแทน</span>
                  <span className="fw-semibold">{fmtNum(substitution.total)} คาบ</span>
                </div>
                <div className="d-flex justify-content-between mt-1">
                  <span className="text-secondary">ครูที่ไปสอนแทน</span>
                  <span className="fw-semibold">{fmtNum(substitution.substitutes)} คน</span>
                </div>
              </div>
            )}
          </Panel>
        </Col>
      </Row>
    </ExecPage>
  );
}
